import { usePostUtilitiesValidatephonenumber } from '@/api/generated/utility/utility'
import type { PhoneApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'
import { mapMemberError } from './memberRequestErrors'

/**
 * Phone-number validation for the new-member (create) page. Replaces the
 * `validatePhoneNumber` thunk + the `VALIDATE_PHONE_NUMBER*` reducer cases.
 *
 * Resolves with the same `{ phoneIsValid, error }` shape the page previously derived from
 * the `newClinicMember` slice: success → `{ phoneIsValid: true }`; a PhoneNumberNotValid /
 * ThirdPartyError failure → `{ phoneIsValid: false }` (matching the old reducer branches).
 */
export interface ValidateMemberPhoneResult {
  phoneIsValid: boolean
  error: string
}

export const useValidateMemberPhone = () => {
  const { mutateAsync, isPending } = usePostUtilitiesValidatephonenumber()

  const validatePhone = async (phone: PhoneApiContract): Promise<ValidateMemberPhoneResult> => {
    try {
      await mutateAsync({ data: phone })
      return { phoneIsValid: true, error: '' }
    } catch (error) {
      const mapped = mapMemberError(error)
      // The old reducer left phoneIsValid === null for non-phone errors; the page only ever
      // treats a definitive `false` as invalid, so collapse null → false here.
      return { phoneIsValid: mapped.phoneIsValid === true, error: mapped.error }
    }
  }

  return { validatePhone, isPending }
}
